import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getMaintenancePageData,
  createMaintenanceSchedule, 
  updateMaintenanceSchedule,
  deleteMaintenanceSchedule,
  completeMaintenanceSchedule,
  exportMaintenanceToExcel,
  MaintenanceSchedule as ApiMaintenanceSchedule,
  Property as ApiProperty
} from '../lib/api';
import { 
  Wrench,
  Plus,
  Calendar,
  Clock,
  House,
  Buildings,
  CheckCircle,
  Warning,
  CaretDown,
  PencilSimple,
  Trash,
  ArrowsClockwise,
  FunnelSimple,
  Bell,
  CalendarCheck,
  Timer,
  X,
  Export
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

// Type definitions for this page
interface MaintenanceSchedule {
  id?: string;
  title?: string;
  description?: string;
  category?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'once';
  interval?: number;
  property_id?: string;
  apartment_id?: string;
  organization_id?: string;
  start_date?: string;
  due_date?: string;
  next_date?: string | Date;
  responsible?: string;
  estimated_duration?: string;
  notes?: string;
  reminder_enabled?: boolean;
  is_completed?: boolean;
  completed_at?: string;
  completed_by?: string;
  completion_notes?: string;
  days_until?: number;
  completion_count?: number;
  last_completed?: string;
}

interface PropertyData {
  id: string;
  name?: string;
}

interface ApartmentData {
  id: string;
  apartment_number?: string;
  property_id?: string;
}

interface FormDataType {
  title: string;
  description: string;
  category: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'once';
  interval: number;
  property_id: string;
  apartment_id: string;
  start_date: string;
  due_date: string;
  responsible: string;
  estimated_duration: string;
  notes: string;
  reminder_enabled?: boolean;
}

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Dagligen', labelEn: 'Daily' },
  { value: 'weekly', label: 'Veckovis', labelEn: 'Weekly' },
  { value: 'monthly', label: 'Månadsvis', labelEn: 'Monthly' },
  { value: 'quarterly', label: 'Kvartalsvis', labelEn: 'Quarterly' },
  { value: 'yearly', label: 'Årligen', labelEn: 'Yearly' },
  { value: 'once', label: 'Engångs', labelEn: 'One-time' }
];

const CATEGORY_OPTIONS = [
  { value: 'inspection', label: 'Besiktning', icon: '🔍' },
  { value: 'cleaning', label: 'Städning', icon: '🧹' },
  { value: 'hvac', label: 'Ventilation/Värme', icon: '🌡️' },
  { value: 'plumbing', label: 'VVS', icon: '🚿' },
  { value: 'electrical', label: 'El', icon: '⚡' },
  { value: 'safety', label: 'Säkerhet', icon: '🔒' },
  { value: 'garden', label: 'Utemiljö', icon: '🌳' },
  { value: 'elevator', label: 'Hiss', icon: '🛗' },
  { value: 'other', label: 'Övrigt', icon: '📋' }
];

export default function MaintenancePage() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<MaintenanceSchedule[]>([]);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [apartments, setApartments] = useState<ApartmentData[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming', 'all', 'overdue'
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceSchedule | null>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    description: '',
    category: 'inspection',
    frequency: 'yearly',
    interval: 1,
    property_id: '',
    apartment_id: '',
    start_date: '',
    due_date: '',
    responsible: '',
    estimated_duration: '',
    notes: '',
    reminder_enabled: true
  });
  
  // Complete form
  const [completeNotes, setCompleteNotes] = useState('');

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // Single batch request for all data
      const data = await getMaintenancePageData(user?.organization_id ?? null);
      setSchedules(data.schedules || []);
      setUpcomingEvents(data.upcomingEvents || []);
      setProperties(data.properties || []);
      setApartments(data.apartments || []);
    } catch (error) {
      console.error('Failed to fetch maintenance data:', error);
      toast.error('Kunde inte hämta underhållsdata');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter schedules
  const filteredSchedules = useMemo(() => {
    let filtered = viewMode === 'upcoming' ? upcomingEvents : schedules;
    
    if (filterProperty !== 'all') {
      filtered = filtered.filter(s => s.property_id === filterProperty);
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(s => s.category === filterCategory);
    }
    
    if (viewMode === 'overdue') {
      filtered = filtered.filter(s => (s.days_until ?? 0) < 0);
    }
    
    return filtered;
  }, [schedules, upcomingEvents, viewMode, filterProperty, filterCategory]);

  // Stats
  const stats = useMemo(() => {
    const overdue = upcomingEvents.filter(e => (e.days_until ?? 0) < 0).length;
    const thisWeek = upcomingEvents.filter(e => (e.days_until ?? 0) >= 0 && (e.days_until ?? 0) <= 7).length;
    const thisMonth = upcomingEvents.filter(e => (e.days_until ?? 0) >= 0 && (e.days_until ?? 0) <= 30).length;
    return { overdue, thisWeek, thisMonth, total: schedules.length };
  }, [upcomingEvents, schedules]);

  const handleCreate = async () => {
    if (!formData.title) {
      toast.error('Ange en titel');
      return;
    }
    
    setSaving(true);
    try {
      await createMaintenanceSchedule({
        ...formData,
        organization_id: user?.organization_id
      });
      toast.success('Underhållsschema skapat');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Kunde inte skapa schema');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (): Promise<void> => {
    if (!selectedSchedule?.id || !formData.title) return;
    
    setSaving(true);
    try {
      await updateMaintenanceSchedule(selectedSchedule.id, formData);
      toast.success('Schema uppdaterat');
      setShowCreateDialog(false);
      setSelectedSchedule(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Kunde inte uppdatera');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (schedule: MaintenanceSchedule): Promise<void> => {
    if (!schedule.id || !confirm(`Ta bort "${schedule.title}"?`)) return;
    
    try {
      await deleteMaintenanceSchedule(schedule.id);
      toast.success('Schema borttaget');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleComplete = async (): Promise<void> => {
    if (!selectedSchedule?.id) return;
    
    setSaving(true);
    try {
      await completeMaintenanceSchedule(selectedSchedule.id, {
        completed_by: user?.id,
        notes: completeNotes,
        title: selectedSchedule.title,
        apartment_id: selectedSchedule.apartment_id,
        property_id: selectedSchedule.property_id,
        organization_id: selectedSchedule.organization_id,
        current_count: selectedSchedule.completion_count || 0
      });
      toast.success('Markerat som utfört');
      setShowCompleteDialog(false);
      setSelectedSchedule(null);
      setCompleteNotes('');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte markera som utfört');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportMaintenanceToExcel(upcomingEvents as ApiMaintenanceSchedule[], { properties: properties as ApiProperty[] });
      toast.success('Export slutförd');
    } catch (error) {
      toast.error('Export misslyckades');
    } finally {
      setExporting(false);
    }
  };

  const openEditDialog = (schedule: MaintenanceSchedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      title: schedule.title || '',
      description: schedule.description || '',
      category: schedule.category || 'inspection',
      frequency: schedule.frequency || 'yearly',
      interval: schedule.interval || 1,
      property_id: schedule.property_id || '',
      apartment_id: schedule.apartment_id || '',
      start_date: schedule.start_date ? schedule.start_date.split('T')[0] : '',
      due_date: schedule.due_date ? schedule.due_date.split('T')[0] : '',
      responsible: schedule.responsible || '',
      estimated_duration: schedule.estimated_duration || '',
      notes: schedule.notes || ''
    });
    setShowCreateDialog(true);
  };

  const openCompleteDialog = (schedule: MaintenanceSchedule) => {
    setSelectedSchedule(schedule);
    setCompleteNotes('');
    setShowCompleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'inspection',
      frequency: 'yearly',
      interval: 1,
      property_id: '',
      apartment_id: '',
      start_date: '',
      due_date: '',
      responsible: '',
      estimated_duration: '',
      notes: '',
      reminder_enabled: true
    });
    setSelectedSchedule(null);
  };

  const getPropertyName = (id: string | undefined): string => properties.find(p => p.id === id)?.name || '';
  const getApartmentNumber = (id: string | undefined): string => apartments.find(a => a.id === id)?.apartment_number || '';
  const getCategoryInfo = (cat: string | undefined) => CATEGORY_OPTIONS.find(c => c.value === cat) || CATEGORY_OPTIONS[8];
  const getFrequencyLabel = (freq: string | undefined): string => FREQUENCY_OPTIONS.find(f => f.value === freq)?.label || freq || '';

  const getStatusColor = (daysUntil: number): string => {
    if (daysUntil < 0) return 'bg-red-100 text-red-700 border-red-200';
    if (daysUntil <= 7) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (daysUntil <= 30) return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const formatDate = (dateStr: string | Date | undefined): string => {
    if (!dateStr) return '-';
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const canEdit = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'board_member';

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-lg" />
            ))}
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-100 flex items-center justify-center">
            <Wrench size={20} className="text-amber-600" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Underhåll</h1>
            <p className="text-sm text-slate-500">Planera och följ upp underhåll</p>
          </div>
        </div>
        <div className="flex gap-2">
          {upcomingEvents.length > 0 && (
            <Button 
              variant="outline" 
              onClick={handleExport} 
              disabled={exporting}
              data-testid="export-maintenance-btn"
            >
              <Export size={18} className="mr-2" />
              {exporting ? 'Exporterar...' : 'Exportera'}
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-schedule-btn">
              <Plus size={18} className="mr-2" />
              Nytt schema
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          className={`card-flat cursor-pointer transition-all ${viewMode === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setViewMode('overdue')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Warning size={20} className="text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.overdue}</div>
              <div className="text-xs text-slate-500">Försenade</div>
            </div>
          </div>
        </div>
        
        <div 
          className={`card-flat cursor-pointer transition-all ${viewMode === 'upcoming' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setViewMode('upcoming')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Bell size={20} className="text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.thisWeek}</div>
              <div className="text-xs text-slate-500">Denna vecka</div>
            </div>
          </div>
        </div>
        
        <div className="card-flat">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar size={20} className="text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.thisMonth}</div>
              <div className="text-xs text-slate-500">Denna månad</div>
            </div>
          </div>
        </div>
        
        <div 
          className={`card-flat cursor-pointer transition-all ${viewMode === 'all' ? 'ring-2 ring-slate-500' : ''}`}
          onClick={() => setViewMode('all')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <ArrowsClockwise size={20} className="text-slate-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-xs text-slate-500">Totalt scheman</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterProperty} onValueChange={setFilterProperty}>
          <SelectTrigger className="w-[180px]" data-testid="property-filter">
            <Buildings size={16} className="mr-2" />
            <SelectValue placeholder="Fastighet" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla fastigheter</SelectItem>
            {properties.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter">
            <FunnelSimple size={16} className="mr-2" />
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla kategorier</SelectItem>
            {CATEGORY_OPTIONS.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.icon} {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Schedule List */}
      {filteredSchedules.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state py-12">
            <Wrench size={48} className="empty-state-icon" />
            <p className="text-slate-500">
              {viewMode === 'overdue' ? 'Inga försenade underhåll' : 
               viewMode === 'upcoming' ? 'Inga kommande underhåll' : 
               'Inga underhållsscheman skapade'}
            </p>
            {canEdit && viewMode === 'all' && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                <Plus size={16} className="mr-2" />
                Skapa första schemat
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSchedules.map(schedule => {
            const catInfo = getCategoryInfo(schedule.category);
            const daysUntil = schedule.days_until ?? 999;
            
            return (
              <div 
                key={schedule.id}
                className="card-flat hover:shadow-md transition-shadow"
                data-testid={`schedule-${schedule.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Category Icon */}
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-xl flex-shrink-0">
                    {catInfo.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-slate-900">{schedule.title}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{schedule.description}</p>
                      </div>
                      
                      {/* Status Badge */}
                      {schedule.next_date && (
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${getStatusColor(daysUntil)}`}>
                          {daysUntil < 0 ? `${Math.abs(daysUntil)} dagar försenad` :
                           daysUntil === 0 ? 'Idag' :
                           daysUntil === 1 ? 'Imorgon' :
                           `Om ${daysUntil} dagar`}
                        </span>
                      )}
                    </div>
                    
                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Timer size={12} />
                        {getFrequencyLabel(schedule.frequency)}
                      </span>
                      
                      {schedule.property_id && (
                        <span className="flex items-center gap-1">
                          <Buildings size={12} />
                          {getPropertyName(schedule.property_id)}
                        </span>
                      )}
                      
                      {schedule.apartment_id && (
                        <span className="flex items-center gap-1">
                          <House size={12} />
                          Lgh {getApartmentNumber(schedule.apartment_id)}
                        </span>
                      )}
                      
                      {schedule.next_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(schedule.next_date)}
                        </span>
                      )}
                      
                      {schedule.last_completed && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={12} />
                          Senast: {formatDate(schedule.last_completed)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openCompleteDialog(schedule)}
                        className="p-2 hover:bg-green-100 rounded text-slate-500 hover:text-green-600"
                        title="Markera som utfört"
                      >
                        <CheckCircle size={18} />
                      </button>
                      <button
                        onClick={() => openEditDialog(schedule)}
                        className="p-2 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                        title="Redigera"
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(schedule)}
                        className="p-2 hover:bg-red-100 rounded text-slate-500 hover:text-red-600"
                        title="Ta bort"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSchedule ? 'Redigera schema' : 'Nytt underhållsschema'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="t.ex. Årlig hissbesiktning"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Beskrivning</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detaljerad beskrivning..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kategori</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Frekvens</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v as typeof prev.frequency }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(freq => (
                      <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {formData.frequency !== 'once' && (
              <div>
                <Label>Intervall (antal perioder)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.interval}
                  onChange={(e) => setFormData(prev => ({ ...prev, interval: parseInt(e.target.value) || 1 }))}
                  className="mt-1 w-24"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fastighet</Label>
                <Select value={formData.property_id || '_none'} onValueChange={(v) => setFormData(prev => ({ ...prev, property_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Välj fastighet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Ingen specifik</SelectItem>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Lägenhet</Label>
                <Select value={formData.apartment_id || '_none'} onValueChange={(v) => setFormData(prev => ({ ...prev, apartment_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Välj lägenhet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Ingen specifik</SelectItem>
                    {apartments.map(a => (
                      <SelectItem key={a.id} value={a.id}>Lgh {a.apartment_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{formData.frequency === 'once' ? 'Deadline' : 'Startdatum'}</Label>
                <Input
                  type="date"
                  value={formData.frequency === 'once' ? formData.due_date : formData.start_date}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    [formData.frequency === 'once' ? 'due_date' : 'start_date']: e.target.value 
                  }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Ansvarig</Label>
                <Input
                  value={formData.responsible}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsible: e.target.value }))}
                  placeholder="t.ex. Fastighetsskötare"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Anteckningar</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Extra information..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            {/* Reminder toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-amber-600" />
                <div>
                  <div className="font-medium text-slate-900 text-sm">Påminnelse</div>
                  <div className="text-xs text-slate-500">Notifiera 7 dagar innan</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, reminder_enabled: !prev.reminder_enabled }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.reminder_enabled ? 'bg-amber-500' : 'bg-slate-300'
                }`}
              >
                <span 
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    formData.reminder_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} 
                />
              </button>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Avbryt
              </Button>
              <Button onClick={selectedSchedule ? handleUpdate : handleCreate} disabled={saving}>
                {saving ? 'Sparar...' : selectedSchedule ? 'Uppdatera' : 'Skapa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Markera som utfört</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900">{selectedSchedule?.title}</h4>
              <p className="text-sm text-slate-500 mt-1">{selectedSchedule?.description}</p>
            </div>
            
            <div>
              <Label>Anteckningar (valfritt)</Label>
              <Textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="t.ex. Inga anmärkningar, allt ok"
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
                Avbryt
              </Button>
              <Button onClick={handleComplete} disabled={saving} className="bg-green-600 hover:bg-green-700">
                <CheckCircle size={16} className="mr-2" />
                {saving ? 'Sparar...' : 'Markera som utfört'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
